// Hayah preferences — single source of truth for all settings the user
// can control across Sleep / Activity / Exercise / Fasting / Calories,
// plus units, accessibility (reduce motion), Apple Health sync, Watch +
// Live Activity, reminders, and data tools.
//
// Persisted as one JSON blob in `localStorage.hayahPrefs`. Components
// subscribe via `useHayahPrefs()`; writes go through `savePrefs()` and
// dispatch a "storage" event so other open components stay in sync.
//
// Smart-fasting helpers (computeFastingWindow, allow-list / tolerance
// logic, milestone catalog, override store) live in this file too so
// HayahToday and HayahSettings share the same code path.

import { useCallback, useEffect, useState } from "react";

// ─── DEFAULTS ─────────────────────────────────────────────────────
export const DEFAULT_PREFS = {
  version: 2,

  sleep: {
    goalHours: 8,
    targetBedtime: "22:30",
    targetWake: "06:30",
    windDownReminderMin: 30,
    windDownEnabled: false,
    debtTracking: true,
    source: "either",
    showStages: true,
  },

  activity: {
    stepsGoal: 12000,
    burnGoal: 3000,
    moveReminderMin: 0,
    stepSourcePriority: "watch",
    hideZeroTiles: false,
    primaryActivityTile: "steps",
  },

  exercise: {
    defaultRestSec: 90,
    audioCue: true,
    hapticCue: true,
    autoProgress: true,
    promptWarmupSets: true,
    defaultBarKg: 20,
    scheduleDays: [1, 3, 5],
    preWorkoutFuelAutoLog: true,
    liveActivityRate: "realtime",
    hrZoneAlerts: false,
    prAutoDetect: true,
    historyRetentionDays: 365,
    volumeGoalPerMuscle: {},
  },

  fasting: {
    enabled: true,
    visibleOnToday: true,
    protocol: "16:8",
    goalHours: 16,
    goodHours: 14,
    okHours: 12,
    bedtimeOffsetMin: 60,
    minLogHours: 8,
    excludeWeekendsFromStreak: false,
    showBenefitsTimeline: true,
    crossLinkIbadahSawm: true,
    notifyOnClose: true,
    notifyOnBrokenEarly: true,
    milestoneNotifications: [12, 16, 18],
    ramadanMode: "auto",
    streakSaveSuggestions: true,
    adaptiveGoal: false,
    calorieTolerance: 50,
    allowList: [
      "coffee", "espresso", "americano", "cappuccino", "long black",
      "black tea", "green tea", "rooibos", "herbal tea",
      "water", "sparkling water", "lemon water",
    ],
    allowListIncludesAddIns: false,
    eatingCutoffTime: "21:00",
    eatingCutoffEnabled: false,
  },

  coach: {
    // Dictation: "auto-send" → fire message on natural silence (true voice mode).
    // "review" → drop dictated text into the input for editing; user taps send.
    voiceMode: "auto-send",
    // Grace period in ms before auto-sending; user can tap-cancel during this
    // window. 0 = no countdown (instant send).
    autoSendCancelMs: 1500,
  },

  calories: {
    defaultMealSlot: "lunch",
    supplementsAsSeparateSlot: true,
    halalOnlyFilter: false,
    frequencyRanking: true,
    heroMode: "remaining",
    netVsGross: "net",
    decimalPrecision: 0,
    macroDisplay: "grams",
    barcodeAction: "search",
    showRestaurantsByDefault: true,
    bodyCompTracking: false,
    waterUnit: "cups",
    weightInReminder: { day: 6, time: "08:00", enabled: false },
  },

  units: { weight: "kg", energy: "kcal", time: "12h" },

  motion: { reduce: false },

  sync: { appleHealthAutoOnFocus: true },

  watch: { routinePush: true, liveActivity: true },

  reminders: {
    enabled: false,
    meals: { breakfast: "07:30", lunch: "12:30", dinner: "18:30", enabled: false },
    water: { everyMin: 90, startAt: "08:00", endAt: "21:00", enabled: false },
    workout: { time: "17:00", days: [1, 3, 5], enabled: false },
    fastingClose: { enabled: true },
    fastingMilestones: { enabled: false },
    windDown: { enabled: false },
    weightIn: { enabled: false },
    move: { enabled: false },
  },
};

const PREFS_KEY = "hayahPrefs";
const OVERRIDES_KEY = "fastingOverrides";

// Deep-merge user prefs over defaults so newly-added fields populate.
function mergePrefs(saved) {
  const out = { ...DEFAULT_PREFS };
  if (!saved || typeof saved !== "object") return out;
  for (const k of Object.keys(DEFAULT_PREFS)) {
    if (saved[k] && typeof saved[k] === "object" && !Array.isArray(saved[k])) {
      out[k] = { ...DEFAULT_PREFS[k], ...saved[k] };
      // Nested objects (one level deep — covers meals, water, weightIn, etc.)
      for (const k2 of Object.keys(DEFAULT_PREFS[k] || {})) {
        const def = DEFAULT_PREFS[k][k2];
        const cur = saved[k][k2];
        if (def && typeof def === "object" && !Array.isArray(def) && cur && typeof cur === "object" && !Array.isArray(cur)) {
          out[k][k2] = { ...def, ...cur };
        }
      }
    } else if (saved[k] !== undefined) {
      out[k] = saved[k];
    }
  }
  return out;
}

export function loadPrefs() {
  try {
    return mergePrefs(JSON.parse(localStorage.getItem(PREFS_KEY) || "null"));
  } catch {
    return mergePrefs(null);
  }
}

export function savePrefs(prefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    // Force a storage event for components in the same tab.
    try { window.dispatchEvent(new StorageEvent("storage", { key: PREFS_KEY })); } catch {}
    // Re-schedule reminders whenever prefs change.
    try { scheduleHayahReminders(prefs); } catch {}
  } catch {}
}

export function patchPrefs(patch) {
  const cur = loadPrefs();
  const next = { ...cur };
  for (const k of Object.keys(patch)) {
    if (patch[k] && typeof patch[k] === "object" && !Array.isArray(patch[k]) && cur[k] && typeof cur[k] === "object") {
      next[k] = { ...cur[k], ...patch[k] };
    } else {
      next[k] = patch[k];
    }
  }
  savePrefs(next);
  return next;
}

// React hook — subscribes to localStorage changes for `hayahPrefs`.
export function useHayahPrefs() {
  const [prefs, setPrefs] = useState(loadPrefs);
  useEffect(() => {
    const reload = () => setPrefs(loadPrefs());
    const onStorage = (e) => { if (!e.key || e.key === PREFS_KEY) reload(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  // OS prefers-reduced-motion auto-respect
  const [osReduceMotion, setOsReduceMotion] = useState(() => {
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
  });
  useEffect(() => {
    try {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      const onChange = () => setOsReduceMotion(mq.matches);
      mq.addEventListener?.("change", onChange);
      return () => mq.removeEventListener?.("change", onChange);
    } catch {}
  }, []);
  const update = useCallback((patch) => setPrefs(patchPrefs(patch)), []);
  const effective = { ...prefs, motion: { reduce: prefs.motion.reduce || osReduceMotion, _osDriven: osReduceMotion && !prefs.motion.reduce } };
  return { prefs: effective, raw: prefs, update };
}

// ─── FORMATTERS ───────────────────────────────────────────────────
export function fmtWeight(kg, unit = "kg") {
  if (kg == null || !Number.isFinite(kg)) return null;
  if (unit === "lbs") return { value: +(kg * 2.20462).toFixed(1), suffix: "lb" };
  return { value: +kg.toFixed(1), suffix: "kg" };
}
export function fmtEnergy(kcal, unit = "kcal") {
  if (kcal == null || !Number.isFinite(kcal)) return null;
  if (unit === "kJ") return { value: Math.round(kcal * 4.184), suffix: "kJ" };
  return { value: Math.round(kcal), suffix: "kcal" };
}
export function fmtClock(ts, timeFmt = "12h") {
  if (!ts) return "—";
  const d = new Date(ts);
  let h = d.getHours();
  const m = d.getMinutes();
  if (timeFmt === "24h") return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const ap = h >= 12 ? "P" : "A";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")}${ap}`;
}
export function fmtHM(hours) {
  if (hours == null || !Number.isFinite(hours)) return "—";
  const total = Math.max(0, Math.round(hours * 60));
  return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, "0")}m`;
}
export function fmtTimeHHMM(hhmm, timeFmt = "12h") {
  if (!hhmm) return "—";
  const [h, m] = hhmm.split(":").map(Number);
  if (timeFmt === "24h") return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
}
export function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ─── FASTING OVERRIDES ────────────────────────────────────────────
export function loadFastingOverrides() {
  try { return JSON.parse(localStorage.getItem(OVERRIDES_KEY) || "{}") || {}; } catch { return {}; }
}
export function saveFastingOverrides(map) {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(map));
    try { window.dispatchEvent(new StorageEvent("storage", { key: OVERRIDES_KEY })); } catch {}
  } catch {}
}
export function patchFastingOverride(dateStr, patch) {
  const map = loadFastingOverrides();
  if (patch == null) {
    delete map[dateStr];
  } else {
    map[dateStr] = { ...(map[dateStr] || {}), ...patch };
  }
  saveFastingOverrides(map);
  return map;
}

// ─── SMART FASTING ────────────────────────────────────────────────
// Returns the fasting window for `dateStr`, applying:
//   - Per-day overrides (deleted / startTs / endTs / note).
//   - Calorie tolerance: meals with calories < tolerance don't end the fast.
//   - Allow-list: meal names matching any allow-list term don't end the fast.
//
// `settings` = {
//   tolerance: number,            // < N kcal doesn't break a fast
//   allowList: string[],          // lowercased substrings; matched case-insensitively
//   bedtimeOffsetMin: number,     // start fallback
// }
export function computeFastingWindow(log, dateStr, overrides = {}, settings = {}) {
  const NULL = { startTs: null, endTs: null, hours: null, source: null, inProgress: false, override: null };
  if (!log || !dateStr) return NULL;

  const ov = overrides[dateStr];
  if (ov && ov.deleted) return { ...NULL, override: ov };

  const d = new Date(`${dateStr}T00:00:00`);
  const prev = new Date(d); prev.setDate(prev.getDate() - 1);
  const prevStr = localDateStr(prev);

  const flatMeals = (entry) => {
    const meals = entry?.meals;
    if (!meals) return [];
    const out = [];
    for (const slot of Object.keys(meals)) {
      const arr = Array.isArray(meals[slot]) ? meals[slot] : [];
      for (const m of arr) out.push(m);
    }
    return out;
  };
  const tsOf = (m) => {
    if (!m) return NaN;
    const t = m.loggedAt ? Date.parse(m.loggedAt) : (m.ts || NaN);
    return Number.isFinite(t) ? t : NaN;
  };
  const tolerance = Number.isFinite(settings.tolerance) ? settings.tolerance : 0;
  const allow = (settings.allowList || []).map(s => String(s || "").toLowerCase()).filter(Boolean);
  const isTolerated = (m) => {
    const kcal = +m.calories || 0;
    if (kcal < tolerance) return true;
    const name = String(m.name || "").toLowerCase();
    for (const a of allow) {
      if (name.includes(a)) return true;
    }
    return false;
  };

  let startTs = null, source = null;
  if (ov && Number.isFinite(ov.startTs)) {
    startTs = ov.startTs;
    source = "manual";
  } else {
    // Only consider meals whose timestamp actually falls within yesterday's
    // 24h window. Back-filling a meal to a past date via History stamps it
    // with the time-of-back-fill, not the meal's real time — so the bucket
    // can contain timestamps from other days. Filter those out so the
    // fast-start doesn't anchor on a Tuesday-morning back-fill that lives
    // in Monday's bucket.
    const yStartMs = +new Date(`${prevStr}T00:00:00`);
    const yEndMs   = yStartMs + 24 * 3600 * 1000;
    // Last *non-tolerated* meal of yesterday — so a late-night cappuccino
    // doesn't pull the fast-start forward.
    const yesterdayMeals = flatMeals(log[prevStr])
      .filter(m => !isTolerated(m))
      .map(tsOf).filter(Number.isFinite)
      .filter(ts => ts >= yStartMs && ts < yEndMs);
    if (yesterdayMeals.length) {
      startTs = Math.max(...yesterdayMeals);
      source = "lastMeal";
    } else {
      const segs = log[dateStr]?.sleepSegments;
      if (Array.isArray(segs) && segs.length) {
        const starts = segs.map(s => Number(s?.s)).filter(Number.isFinite);
        if (starts.length) {
          const offsetMs = (Number.isFinite(settings.bedtimeOffsetMin) ? settings.bedtimeOffsetMin : 60) * 60 * 1000;
          startTs = Math.min(...starts) + offsetMs;
          source = "bedtime+offset";
        }
      }
    }
  }
  if (startTs == null) return NULL;

  let endTs = null, inProgress = false, hours;
  if (ov && Number.isFinite(ov.endTs)) {
    endTs = ov.endTs;
  } else {
    // Same clamp on today's bucket — back-fills to *today* from a previous
    // session would otherwise land here too.
    const tStartMs = +new Date(`${dateStr}T00:00:00`);
    const tEndMs   = tStartMs + 24 * 3600 * 1000;
    const todayBreaks = flatMeals(log[dateStr])
      .filter(m => !isTolerated(m))
      .map(tsOf).filter(Number.isFinite)
      .filter(ts => ts >= tStartMs && ts < tEndMs);
    if (todayBreaks.length) {
      endTs = Math.min(...todayBreaks);
    } else {
      inProgress = true;
    }
  }
  if (inProgress) {
    hours = (Date.now() - startTs) / 3600000;
  } else {
    hours = (endTs - startTs) / 3600000;
  }
  if (!Number.isFinite(hours) || hours < 0 || hours > 48) return NULL;

  return { startTs, endTs, hours, source, inProgress, override: ov || null, note: ov?.note || "" };
}

// ─── MILESTONES ───────────────────────────────────────────────────
export const FASTING_MILESTONES = [
  { h: 3,  label: "Glycogen drawdown" },
  { h: 8,  label: "Fat-burning starts" },
  { h: 12, label: "Ketones rising" },
  { h: 14, label: "Autophagy onset" },
  { h: 16, label: "Growth-hormone bump" },
  { h: 18, label: "Deep autophagy" },
  { h: 24, label: "Stem-cell renewal" },
];

export function nextMilestone(hours) {
  if (hours == null) return FASTING_MILESTONES[0];
  return FASTING_MILESTONES.find(m => m.h > hours) || null;
}

// ─── FASTING STAGES ───────────────────────────────────────────────
// Coarse physiological bands the user moves through during a fast.
// Slug strings match hayahFastToast.js tiers so the AI Coach can reason
// about them consistently. Cutoffs are "approximate" — individual liver
// glycogen, last-meal size, and training state shift them ±2h.
export const FASTING_STAGES = [
  { slug: "digesting", h0: 0,  h1: 4,  label: "Digesting",         body: "Insulin rising. Body is processing the meal." },
  { slug: "glycogen",  h0: 4,  h1: 12, label: "Glycogen drawdown", body: "Liver glycogen falling. Insulin returning to baseline." },
  { slug: "ketosis",   h0: 12, h1: 14, label: "Ketosis onset",     body: "Glycogen depleted. Fat-burning ramps; ketones begin rising." },
  { slug: "autophagy", h0: 14, h1: 16, label: "Autophagy",         body: "Cellular cleanup activates. Mental clarity often peaks here." },
  { slug: "deep",      h0: 16, h1: Infinity, label: "Deep repair", body: "Growth hormone surge, stem-cell signalling. Don't push past hunger cues." },
];

export function stageForHours(hours) {
  if (hours == null || !Number.isFinite(hours)) return FASTING_STAGES[0];
  return FASTING_STAGES.find(s => hours >= s.h0 && hours < s.h1) || FASTING_STAGES[FASTING_STAGES.length - 1];
}

export function nextStage(hours) {
  if (hours == null || !Number.isFinite(hours)) return FASTING_STAGES[1];
  return FASTING_STAGES.find(s => s.h0 > hours) || null;
}

// ─── CURRENT MEAL / EATING WINDOW STATE ───────────────────────────
// Companion to computeFastingWindow. Where computeFastingWindow tells
// you about the COMPLETED fast (yesterday → today's first meal), this
// tells you about TODAY: when was your most-recent meal, how long
// you've been since it, what stage that puts you in if you keep
// fasting from here, and whether the eating window is still open.
//
// Returns:
//   { mode: "fasting" | "eating",
//     lastMealTs, lastMealName, lastMealSlot,
//     hoursSinceLastMeal,
//     stage, nextStage, hoursToNextStage,
//     eatingWindow: { state: "open"|"closed"|"n/a", cutoffTs, msToCutoff },
//     mealsTodayCount }
//
// "mode = eating" when the user has logged at least one non-tolerated
// meal today AND the eating cutoff hasn't passed (or no cutoff set).
// "mode = fasting" when no meals today yet, or we're past the cutoff.
export function computeCurrentMealWindow(log, dateStr, prefs = {}, now = Date.now()) {
  const NULL = {
    mode: "fasting", lastMealTs: null, lastMealName: null, lastMealSlot: null,
    hoursSinceLastMeal: null, stage: null, nextStage: null, hoursToNextStage: null,
    eatingWindow: { state: "n/a", cutoffTs: null, msToCutoff: null },
    mealsTodayCount: 0,
  };
  if (!log || !dateStr) return NULL;

  const fasting = prefs?.fasting || {};
  const tolerance = Number.isFinite(fasting.calorieTolerance) ? fasting.calorieTolerance : 50;
  const allow = (fasting.allowList || []).map(s => String(s || "").toLowerCase()).filter(Boolean);
  const isTolerated = (m) => {
    const kcal = +m.calories || 0;
    if (kcal < tolerance) return true;
    const name = String(m.name || "").toLowerCase();
    for (const a of allow) { if (name.includes(a)) return true; }
    return false;
  };

  const meals = log[dateStr]?.meals || {};
  const tStartMs = +new Date(`${dateStr}T00:00:00`);
  const tEndMs   = tStartMs + 24 * 3600 * 1000;
  let bestTs = -Infinity, bestMeal = null, bestSlot = null, nonTolCount = 0;
  for (const slot of Object.keys(meals)) {
    const arr = Array.isArray(meals[slot]) ? meals[slot] : [];
    for (const m of arr) {
      if (isTolerated(m)) continue;
      const t = m.loggedAt ? Date.parse(m.loggedAt) : (m.ts || NaN);
      if (!Number.isFinite(t) || t < tStartMs || t >= tEndMs) continue;
      nonTolCount += 1;
      if (t > bestTs) { bestTs = t; bestMeal = m; bestSlot = slot; }
    }
  }
  if (!Number.isFinite(bestTs)) return { ...NULL, mealsTodayCount: 0 };

  const hoursSince = (now - bestTs) / 3600000;
  const stage = stageForHours(hoursSince);
  const next  = nextStage(hoursSince);
  const hoursToNext = next ? Math.max(0, next.h0 - hoursSince) : null;

  // Eating-window cutoff. "20:00" → tonight's 20:00 in local time.
  const cutoffEnabled = !!fasting.eatingCutoffEnabled;
  const cutoffStr = String(fasting.eatingCutoffTime || "").trim(); // "HH:MM"
  let cutoffTs = null, ewState = "n/a", msToCutoff = null;
  if (cutoffEnabled && /^\d{1,2}:\d{2}$/.test(cutoffStr)) {
    const [hh, mm] = cutoffStr.split(":").map(n => parseInt(n, 10));
    if (Number.isFinite(hh) && Number.isFinite(mm)) {
      cutoffTs = +new Date(`${dateStr}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`);
      msToCutoff = cutoffTs - now;
      ewState = msToCutoff > 0 ? "open" : "closed";
    }
  } else {
    ewState = "open"; // no cutoff configured → treat as open while in eating mode
  }

  // Mode resolution. If we've eaten today and we're pre-cutoff (or no
  // cutoff), we're in the eating window. If post-cutoff, we've already
  // started today's fast — flip to fasting mode (the "since last meal"
  // counter is then the fast counter).
  const mode = (cutoffEnabled && ewState === "closed") ? "fasting" : "eating";

  return {
    mode,
    lastMealTs: bestTs,
    lastMealName: bestMeal?.name || null,
    lastMealSlot: bestSlot,
    hoursSinceLastMeal: hoursSince,
    stage,
    nextStage: next,
    hoursToNextStage: hoursToNext,
    eatingWindow: { state: ewState, cutoffTs, msToCutoff },
    mealsTodayCount: nonTolCount,
  };
}

// ─── PROTOCOL PRESETS ─────────────────────────────────────────────
export const FASTING_PROTOCOLS = {
  "14:10": { okHours: 10, goodHours: 12, goalHours: 14 },
  "16:8":  { okHours: 12, goodHours: 14, goalHours: 16 },
  "18:6":  { okHours: 14, goodHours: 16, goalHours: 18 },
  "20:4":  { okHours: 16, goodHours: 18, goalHours: 20 },
  "OMAD":  { okHours: 18, goodHours: 22, goalHours: 23 },
};

export function applyProtocol(prefs, protocolKey) {
  const p = FASTING_PROTOCOLS[protocolKey];
  if (!p) return prefs;
  return { ...prefs, fasting: { ...prefs.fasting, protocol: protocolKey, ...p } };
}

// ─── REMINDER SCHEDULER ───────────────────────────────────────────
// Uses @capacitor/local-notifications when available (native), falls
// back to web Notification + setTimeout otherwise. Pure best-effort:
// if neither is available, all calls no-op.

const WEB_TIMERS = new Map();
let CAP_NOTIFS = null;
function getCapNotifs() {
  if (CAP_NOTIFS !== null) return CAP_NOTIFS;
  try {
    const plugins = (typeof window !== "undefined") && window.Capacitor?.Plugins;
    CAP_NOTIFS = plugins?.LocalNotifications || false;
  } catch { CAP_NOTIFS = false; }
  return CAP_NOTIFS;
}

const ID_BASE = {
  MEAL_BREAKFAST: 7001,
  MEAL_LUNCH:     7002,
  MEAL_DINNER:    7003,
  WATER:          7100, // 7100..7199 (water repeats)
  WORKOUT:        7200, // 7200..7206 (per day)
  WIND_DOWN:      7300,
  WEIGHT_IN:      7400,
  MOVE:           7500,
};

export async function requestNotificationPermission() {
  const cap = getCapNotifs();
  if (cap?.requestPermissions) {
    try { const r = await cap.requestPermissions(); return r.display === "granted"; } catch { return false; }
  }
  try {
    if (typeof Notification === "undefined") return false;
    if (Notification.permission === "granted") return true;
    const p = await Notification.requestPermission();
    return p === "granted";
  } catch { return false; }
}

export async function cancelHayahReminders() {
  const cap = getCapNotifs();
  if (cap?.cancel) {
    try {
      const ids = [];
      Object.values(ID_BASE).forEach(b => { for (let i = 0; i < 100; i++) ids.push({ id: b + i }); });
      await cap.cancel({ notifications: ids });
    } catch {}
  }
  for (const t of WEB_TIMERS.values()) clearTimeout(t);
  WEB_TIMERS.clear();
}

function nextOccurrence(hhmm, fromDate = new Date()) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(fromDate);
  d.setHours(h, m, 0, 0);
  if (d <= fromDate) d.setDate(d.getDate() + 1);
  return d;
}

function scheduleOne({ id, title, body, at }) {
  if (!at) return;
  const cap = getCapNotifs();
  if (cap?.schedule) {
    try {
      cap.schedule({ notifications: [{ id, title, body, schedule: { at } }] });
      return;
    } catch {}
  }
  // Web fallback
  const delay = at.getTime() - Date.now();
  if (delay <= 0) return;
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const t = setTimeout(() => {
    try { new Notification(title, { body }); } catch {}
  }, Math.min(delay, 2147483000)); // setTimeout max ~24.8 days
  WEB_TIMERS.set(id, t);
}

export async function scheduleHayahReminders(prefs) {
  if (typeof window === "undefined") return;
  await cancelHayahReminders();
  if (!prefs?.reminders?.enabled) return;
  const r = prefs.reminders;
  const now = new Date();

  if (r.meals?.enabled) {
    if (r.meals.breakfast) scheduleOne({ id: ID_BASE.MEAL_BREAKFAST, title: "Breakfast",
      body: "Time to break your fast or fuel up.", at: nextOccurrence(r.meals.breakfast, now) });
    if (r.meals.lunch)     scheduleOne({ id: ID_BASE.MEAL_LUNCH, title: "Lunch",
      body: "Log your midday meal in Ghidha.", at: nextOccurrence(r.meals.lunch, now) });
    if (r.meals.dinner)    scheduleOne({ id: ID_BASE.MEAL_DINNER, title: "Dinner",
      body: "Log your evening meal in Ghidha.", at: nextOccurrence(r.meals.dinner, now) });
  }

  if (r.water?.enabled && r.water.everyMin > 0) {
    const start = nextOccurrence(r.water.startAt, now);
    const endHHMM = r.water.endAt || "21:00";
    const [eh, em] = endHHMM.split(":").map(Number);
    let cursor = new Date(start);
    let i = 0;
    while (i < 80) {
      const end = new Date(cursor); end.setHours(eh, em, 0, 0);
      if (cursor > end) break;
      scheduleOne({ id: ID_BASE.WATER + i, title: "Hydrate", body: "Time for water.", at: cursor });
      cursor = new Date(cursor.getTime() + r.water.everyMin * 60000);
      i++;
    }
  }

  if (r.workout?.enabled && Array.isArray(r.workout.days)) {
    for (const dow of r.workout.days) {
      const next = new Date(now);
      const cur = next.getDay();
      let add = (dow - cur + 7) % 7;
      const target = nextOccurrence(r.workout.time, now);
      if (add === 0 && target <= now) add = 7;
      target.setDate(target.getDate() + add);
      scheduleOne({ id: ID_BASE.WORKOUT + dow, title: "Workout time",
        body: "Riyadah is ready when you are.", at: target });
    }
  }

  if (r.windDown?.enabled && prefs.sleep?.targetBedtime) {
    const bed = nextOccurrence(prefs.sleep.targetBedtime, now);
    bed.setMinutes(bed.getMinutes() - (prefs.sleep.windDownReminderMin || 30));
    if (bed > now) scheduleOne({ id: ID_BASE.WIND_DOWN, title: "Wind down",
      body: "Bedtime soon — close your screens.", at: bed });
  }

  if (r.weightIn?.enabled && prefs.calories?.weightInReminder) {
    const w = prefs.calories.weightInReminder;
    const next = nextOccurrence(w.time, now);
    const cur = next.getDay();
    let add = (w.day - cur + 7) % 7;
    if (add === 0 && next <= now) add = 7;
    next.setDate(next.getDate() + add);
    scheduleOne({ id: ID_BASE.WEIGHT_IN, title: "Weigh-in",
      body: "Time for your weekly weigh-in.", at: next });
  }

  if (r.move?.enabled && prefs.activity?.moveReminderMin > 0) {
    const at = new Date(now.getTime() + prefs.activity.moveReminderMin * 60000);
    scheduleOne({ id: ID_BASE.MOVE, title: "Move",
      body: "Stretch your legs for a minute.", at });
  }
}

// Fasting milestone notifications — scheduled dynamically from a live
// fast since `startTs` and `goalHours` can shift mid-fast on edit.
const FIRED_MS_KEY = "_hayahFastingFiredMs";
export function maybeFireFastingMilestones(prefs, fast) {
  if (!prefs?.reminders?.enabled) return;
  if (!fast?.inProgress || !fast?.startTs) return;
  let fired;
  try { fired = JSON.parse(sessionStorage.getItem(FIRED_MS_KEY) || "{}"); } catch { fired = {}; }
  const startKey = String(fast.startTs);
  const seen = new Set(fired[startKey] || []);
  const milestones = prefs.reminders.fastingMilestones?.enabled
    ? (prefs.fasting?.milestoneNotifications || [])
    : [];
  const closeTarget = prefs.reminders.fastingClose?.enabled
    ? [prefs.fasting?.goalHours]
    : [];
  const targets = [...milestones, ...closeTarget].filter(Number.isFinite);
  const cur = fast.hours;
  for (const h of targets) {
    if (cur >= h && !seen.has(h)) {
      seen.add(h);
      try {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          const ms = FASTING_MILESTONES.find(m => m.h === h);
          new Notification(ms ? `${h}h · ${ms.label}` : `${h}h fast hit`, {
            body: `You've been fasting for ${h} hours.`,
          });
        } else {
          const cap = getCapNotifs();
          if (cap?.schedule) cap.schedule({ notifications: [{
            id: 7900 + h, title: `${h}h fast hit`,
            body: `You've been fasting for ${h} hours.`,
            schedule: { at: new Date(Date.now() + 100) },
          }] });
        }
      } catch {}
    }
  }
  fired[startKey] = Array.from(seen);
  try { sessionStorage.setItem(FIRED_MS_KEY, JSON.stringify(fired)); } catch {}
}

// Stage-transition notifications — fires the moment the user crosses
// from one FASTING_STAGES band to the next (4→glycogen, 12→ketosis,
// 14→autophagy, 16→deep). Anchored to `lastMealTs` so a single meal log
// can drive a full day's worth of nudges. Gated by the same
// `reminders.fastingMilestones` toggle as the existing goal-hours
// notifier — so users who already opted into milestone pings get this
// for free with no extra setting.
const FIRED_STAGE_KEY = "_hayahFastingFiredStages";
export function maybeFireStageMilestones(prefs, currentMeal) {
  if (!prefs?.reminders?.enabled) return;
  if (!prefs?.reminders?.fastingMilestones?.enabled) return;
  if (!currentMeal?.lastMealTs || !Number.isFinite(currentMeal?.hoursSinceLastMeal)) return;
  let fired;
  try { fired = JSON.parse(sessionStorage.getItem(FIRED_STAGE_KEY) || "{}"); } catch { fired = {}; }
  const anchorKey = String(currentMeal.lastMealTs);
  const seen = new Set(fired[anchorKey] || []);
  const cur = currentMeal.hoursSinceLastMeal;
  // Skip the digesting → glycogen 4h transition by default — too noisy
  // for daytime; user's first stage notification should land at ketosis
  // (12h), which is the moment they care about.
  for (const stage of FASTING_STAGES) {
    if (stage.slug === "digesting") continue;
    if (cur >= stage.h0 && !seen.has(stage.slug)) {
      seen.add(stage.slug);
      try {
        const title = `${Math.floor(stage.h0)}h · ${stage.label}`;
        const body  = stage.body;
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification(title, { body });
        } else {
          const cap = getCapNotifs();
          if (cap?.schedule) cap.schedule({ notifications: [{
            id: 7950 + Math.floor(stage.h0),
            title, body,
            schedule: { at: new Date(Date.now() + 100) },
          }] });
        }
      } catch {}
    }
  }
  fired[anchorKey] = Array.from(seen);
  try { sessionStorage.setItem(FIRED_STAGE_KEY, JSON.stringify(fired)); } catch {}
}

// ─── ADAPTIVE GOAL ────────────────────────────────────────────────
// Returns a suggestion object when the last 7 days' average exceeds
// goalHours + 0.5, gated by a once-per-week check stored in prefs.
export function checkAdaptiveGoal(prefs, history) {
  if (!prefs?.fasting?.adaptiveGoal) return null;
  const lastCheck = prefs.fasting._lastAdaptCheck || 0;
  if (Date.now() - lastCheck < 7 * 24 * 3600000) return null;
  const last7 = history.filter(d => d.hours != null).slice(-7);
  if (last7.length < 7) return null;
  const avg = last7.reduce((s, d) => s + d.hours, 0) / 7;
  const goal = prefs.fasting.goalHours;
  if (avg < goal + 0.5) return null;
  return { suggested: +(goal + 0.5).toFixed(1), avg: +avg.toFixed(2) };
}

// ─── DATA EXPORT / RESET ──────────────────────────────────────────
export const HAYAH_DATA_KEYS = [
  // Core food/calories
  "calorieLog", "calorieGoals", "calorieWeightLog",
  "caloriesFavorites", "calorieFrequency", "ghidhaProfile",
  "ghidhaUserFoods", "ghidhaMigrationV", "calorieSearchRecents",
  // Exercise
  "sirat-workouts",
  // Fasting
  "fastingLog", "fastingOverrides", "hayahFastEvents",
  // Prefs / profile / AI coach
  "hayahPrefs", "hayahCoachHistory", "hayahCoachAccess",
  "hayahUserBio", "hayahUserRegion", "hayahFaithContext",
  "hayahPatternInsights", "aiCoachReport_v1", "hayahTokenUsage",
  // Niche trackers
  "junkFreezesUsed", "ketoneLog", "mounjaroCycle", "muscleReadiness",
];

// Lowercase prefixes that identify Hayah-owned data, so the full-snapshot export
// captures accumulated history even if a key isn't enumerated above.
const HAYAH_KEY_PREFIXES = [
  "calorie", "ghidha", "hayah", "fasting", "ketone", "mounjaro",
  "muscle", "readiness", "junk", "riyadah", "aicoach", "sirat-workout",
];

function isHayahOwnedKey(k) {
  if (!k) return false;
  if (HAYAH_DATA_KEYS.includes(k)) return true;
  const lk = k.toLowerCase();
  return HAYAH_KEY_PREFIXES.some((p) => lk.startsWith(p));
}

// Full-snapshot export (version 3) — captures EVERY Hayah-owned key as a raw
// string so nothing is lost. The Pulse app imports this format directly.
export function buildHayahExport({ full = true } = {}) {
  const store = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (full ? isHayahOwnedKey(k) : HAYAH_DATA_KEYS.includes(k)) {
        const v = localStorage.getItem(k);
        if (v != null) store[k] = v;
      }
    }
  } catch {}
  for (const k of HAYAH_DATA_KEYS) {
    if (store[k] == null) {
      try { const v = localStorage.getItem(k); if (v != null) store[k] = v; } catch {}
    }
  }
  return { app: "sirat", kind: "hayah-backup", exportedAt: new Date().toISOString(), version: 3, store };
}

// iOS-safe export. The old `<a download>` blob trick does NOTHING inside an iOS
// WKWebView (no save dialog, no error). So: try the native Share sheet with a
// file (iOS 15+ → Save to Files / AirDrop), then clipboard, then fall back to
// the blob download (desktop / Android). Pure web APIs → ships via OTA.
export async function downloadHayahExport() {
  const data = buildHayahExport();
  const json = JSON.stringify(data, null, 2);
  const filename = `hayah-backup-${localDateStr()}.json`;
  // 1) Web Share API with a file
  try {
    if (typeof File !== "undefined" && navigator.canShare) {
      const file = new File([json], filename, { type: "application/json" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Hayah backup" });
        return "shared";
      }
    }
  } catch (e) { if (e && e.name === "AbortError") return "cancelled"; }
  // 2) Clipboard fallback
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(json);
      try { alert("Backup copied to clipboard."); } catch {}
      return "clipboard";
    }
  } catch (e) {}
  // 3) Blob download (desktop / Android)
  try {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return "downloaded";
  } catch (e) { return "failed"; }
}

// Lossless, format-tolerant restore: accepts v3 {store}, legacy v2 keyed, or a
// raw localStorage dump. Returns the number of keys written.
export function importHayahData(json) {
  if (!json || typeof json !== "object") throw new Error("Invalid backup file");
  let restored = 0;
  const setRaw = (k, raw) => {
    if (k == null || raw == null) return;
    try { localStorage.setItem(k, typeof raw === "string" ? raw : JSON.stringify(raw)); restored++; } catch {}
  };
  const source = (json.store && typeof json.store === "object") ? json.store : json;
  const META = new Set(["exportedAt", "version", "app", "kind", "store"]);
  for (const [k, v] of Object.entries(source)) {
    if (META.has(k) || v == null) continue;
    setRaw(k, v);
  }
  try { window.dispatchEvent(new StorageEvent("storage", { key: null })); } catch {}
  return restored;
}

export function clearHayahData(keys = HAYAH_DATA_KEYS) {
  for (const k of keys) {
    try { localStorage.removeItem(k); } catch {}
  }
  try { window.dispatchEvent(new StorageEvent("storage", { key: null })); } catch {}
}

// ─── DEMO DATA SEEDER ────────────────────────────────────────────
// Writes realistic-looking 30 days of HealthKit-style data into
// calorieLog so the UI on web / dev (where Apple Health isn't
// available) has something to render. Touches:
//   steps, caloriesBurned, basalBurned, sleepMinutes, deepMinutes,
//   remMinutes, awakeMinutes, sleepSegments (per-stage timeline),
//   bodyMassKg
// Preserves existing meals if any.
export function seedDemoAppleHealth({ days = 30 } = {}) {
  const rand = (lo, hi) => Math.round(lo + Math.random() * (hi - lo));
  const choice = arr => arr[Math.floor(Math.random() * arr.length)];
  let log = {};
  try { log = JSON.parse(localStorage.getItem("calorieLog") || "{}") || {}; } catch {}
  const now = new Date();
  for (let i = 0; i < days; i++) {
    const d = new Date(now); d.setDate(d.getDate() - i);
    const ds = localDateStr(d);
    const prev = log[ds] || {};
    const isToday = i === 0;
    // Build a plausible bedtime the night before this date and a wake
    // in the morning of `ds`. Sleep totals roll up from segments so
    // the hypnogram has data too.
    const bedHr = choice([22, 22, 23, 23, 23, 0]);                       // 22:00–00:00
    const bedMin = rand(0, 59);
    const bed = new Date(d); bed.setDate(bed.getDate() - 1);
    bed.setHours(bedHr === 0 ? 24 : bedHr, bedMin, 0, 0);
    const totalSleepMin = rand(360, 480);                                // 6-8h
    const segments = [];
    let cursor = bed.getTime();
    let remaining = totalSleepMin;
    while (remaining > 0) {
      const stage = choice(["ASLEEPCORE","ASLEEPCORE","ASLEEPCORE","ASLEEPDEEP","ASLEEPREM","AWAKE"]);
      const segMin = Math.min(remaining, rand(stage === "ASLEEPDEEP" ? 8 : stage === "AWAKE" ? 2 : 12,
                                              stage === "ASLEEPDEEP" ? 35 : stage === "AWAKE" ? 14 : 75));
      segments.push({ st: stage, s: cursor, e: cursor + segMin * 60000 });
      cursor += segMin * 60000;
      if (stage !== "AWAKE") remaining -= segMin;
    }
    const sumByStage = (m) => segments.filter(s => s.st === m).reduce((t, s) => t + (s.e - s.s) / 60000, 0);
    const deep  = Math.round(sumByStage("ASLEEPDEEP"));
    const rem   = Math.round(sumByStage("ASLEEPREM"));
    const awake = Math.round(sumByStage("AWAKE"));
    const sleepMins = Math.round(segments.filter(s => s.st !== "AWAKE").reduce((t, s) => t + (s.e - s.s) / 60000, 0));

    log[ds] = {
      ...prev,
      steps: prev.steps ?? rand(5500, 14500),
      caloriesBurned: prev.caloriesBurned ?? rand(280, 760),
      basalBurned:    prev.basalBurned    ?? rand(1500, 1900),
      sleepMinutes:   prev.sleepMinutes   ?? sleepMins,
      deepMinutes:    prev.deepMinutes    ?? deep,
      remMinutes:     prev.remMinutes     ?? rem,
      awakeMinutes:   prev.awakeMinutes   ?? awake,
      sleepSegments:  prev.sleepSegments  ?? segments,
      bodyMassKg:     prev.bodyMassKg     ?? +((rand(73, 78) + Math.random() * 0.9).toFixed(1)),
      // Don't replace meals — preserve whatever the user has logged.
      meals: prev.meals || { breakfast: [], lunch: [], dinner: [], snacks: [], supplements: [] },
    };
    // For today only, drop a couple of sample meals if the user has none,
    // so fasting / Today rings light up.
    if (isToday) {
      const meals = log[ds].meals;
      const anyMeal = Object.values(meals).some(arr => Array.isArray(arr) && arr.length > 0);
      if (!anyMeal) {
        const t1 = new Date(d); t1.setHours(8, 15, 0, 0);
        const t2 = new Date(d); t2.setHours(13, 0, 0, 0);
        meals.breakfast = [{
          id: "demo-bf-" + ds, name: "Oats with banana", calories: 380, protein: 14, carbs: 62, fat: 8,
          fiber: 7, sugar: 22, sodium: 60, servingG: 280, servingLabel: "1 bowl",
          loggedAt: t1.toISOString(),
        }];
        meals.lunch = [{
          id: "demo-ln-" + ds, name: "Grilled chicken & rice", calories: 620, protein: 48, carbs: 70, fat: 14,
          fiber: 4, sugar: 3, sodium: 480, servingG: 350, servingLabel: "1 plate",
          loggedAt: t2.toISOString(),
        }];
      }
    }
  }
  try {
    localStorage.setItem("calorieLog", JSON.stringify(log));
    localStorage.setItem("ghidhaLastHealthSyncAt", String(Date.now()));
    window.dispatchEvent(new StorageEvent("storage", { key: "calorieLog" }));
  } catch {}
}
